import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import {
  PRODUCT_TYPES,
  formatQuantity,
  formatOrderWhen,
  formatOrderDate,
  formatPickupDateForInput,
  orderFulfillmentLabel,
  orderRecipientName,
  yesNo,
} from './orderUtils';

const lineItemName = (line) => {
  const model = line.product?.model || 'Item';
  const code = line.product_variant?.code;
  return code ? `${model}-${code}` : model;
};

const lineItemDetails = (line) => {
  if (line.concentration) return `${line.concentration}%`;
  return '';
};

const productTypeLabel = (order) => {
  const category = order.line_items?.find((li) => li?.product)?.product?.category;
  if (category == null) return '';
  return PRODUCT_TYPES.find((t) => t.key === Number(category))?.label || '';
};

const displayValue = (value) => {
  if (value == null || value === '') return '—';
  return value;
};

const FactGrid = ({ facts }) => {
  const items = facts.filter(Boolean);
  if (!items.length) return null;
  return (
    <dl className="order-details-facts">
      {items.map((fact) => (
        <div key={fact.label} className="order-details-fact">
          <dt>{fact.label}</dt>
          <dd>{fact.value}</dd>
        </div>
      ))}
    </dl>
  );
};

const OrderDetailsModal = ({ order, onClose }) => {
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  if (!order) return null;

  const recipient = orderRecipientName(order);
  const submittedAt = formatOrderWhen(order.updated_at || order.created_at, { withYear: true });
  const createdAt = formatOrderWhen(order.created_at, { withYear: true });
  const fulfillment = orderFulfillmentLabel(order);
  const typeLabel = productTypeLabel(order);
  const lines = order.line_items || [];
  const title = recipient && recipient !== 'Order' ? recipient : `Order #${order.id}`;
  const destination = order.address?.description
    || order.address?.full
    || (order.delivery ? 'See notes' : '');

  const overviewFacts = [
    { label: 'Order', value: `#${order.id}` },
    { label: 'Recipient', value: recipient },
    typeLabel ? { label: 'Type', value: typeLabel } : null,
    { label: 'Submitted', value: submittedAt || '—' },
    createdAt && createdAt !== submittedAt
      ? { label: 'Created', value: createdAt }
      : null,
    { label: 'Placed by', value: displayValue(order.placed_by) },
    { label: 'Platform', value: displayValue(order.platform) },
  ];

  const fulfillmentFacts = [
    { label: 'Method', value: order.delivery ? 'Delivery' : 'Pickup' },
    order.delivery
      ? { label: 'Destination', value: displayValue(destination) }
      : { label: 'Pickup date', value: displayValue(formatPickupDateForInput(order.pickup_date)) },
    order.customer
      ? { label: 'Charge', value: order.charge ? 'Charge to customer' : 'No charge' }
      : null,
    { label: 'Fat Albert', value: yesNo(order.fat_albert) },
  ];

  const statusFacts = [
    { label: 'Processed', value: yesNo(order.processed) },
    { label: 'Filled', value: yesNo(order.filled) },
    { label: 'Acknowledged', value: yesNo(order.acknowledged) },
    { label: 'Shipped', value: yesNo(order.shipped) },
  ];

  const referenceFacts = [
    { label: 'PO number', value: displayValue(order.po_number) },
    { label: 'SO number', value: displayValue(order.so_number) },
    { label: 'Customer PO', value: displayValue(order.customer_po) },
    order.purchase_order_number
      ? { label: 'Purchase order', value: order.purchase_order_number }
      : null,
  ];

  const shippingFacts = [
    { label: 'Est. ship date', value: displayValue(formatOrderDate(order.est_ship_date)) },
    { label: 'Carrier', value: displayValue(order.shipping_carrier) },
    { label: 'Tracking', value: displayValue(order.tracking_number) },
  ];

  return createPortal(
    <div
      className="order-details-modal-backdrop"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="order-details-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-details-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="order-details-modal-header">
          <div>
            <h2 id="order-details-title" className="order-details-modal-title">{title}</h2>
            <p className="order-details-modal-subtitle">
              {fulfillment}
              {lines.length
                ? ` · ${lines.length} line${lines.length === 1 ? '' : 's'}`
                : ''}
              {submittedAt ? ` · ${submittedAt}` : ''}
            </p>
          </div>
          <button
            type="button"
            className="order-details-modal-close"
            onClick={onClose}
            aria-label="Close order details"
          >
            <CloseIcon />
          </button>
        </header>

        <div className="order-details-modal-body">
          <section className="order-details-section" aria-label="Overview">
            <h3 className="order-details-section-title">Overview</h3>
            <FactGrid facts={overviewFacts} />
          </section>

          <section className="order-details-section" aria-label="Fulfillment">
            <h3 className="order-details-section-title">Fulfillment</h3>
            <FactGrid facts={fulfillmentFacts} />
          </section>

          <section className="order-details-section" aria-label="Status">
            <h3 className="order-details-section-title">Status</h3>
            <FactGrid facts={statusFacts} />
          </section>

          <section className="order-details-section" aria-label="References">
            <h3 className="order-details-section-title">References</h3>
            <FactGrid facts={referenceFacts} />
          </section>

          <section className="order-details-section" aria-label="Shipping">
            <h3 className="order-details-section-title">Shipping</h3>
            <FactGrid facts={shippingFacts} />
          </section>

          <section className="order-details-section" aria-label="Line items">
            <h3 className="order-details-section-title">Line items</h3>
            {lines.length === 0 ? (
              <p className="order-details-empty">No line items on this order.</p>
            ) : (
              <ul className="order-details-lines">
                {lines.map((line) => (
                  <li key={line.id || lineItemName(line)} className="order-details-line">
                    <div className="order-details-line-main">
                      <span className="order-details-line-name">{lineItemName(line)}</span>
                      {line.product?.description ? (
                        <span className="order-details-line-desc">{line.product.description}</span>
                      ) : null}
                      {lineItemDetails(line) ? (
                        <span className="order-details-line-detail">{lineItemDetails(line)}</span>
                      ) : null}
                    </div>
                    <span className="order-details-line-qty">
                      {formatQuantity({
                        item: line.product || {},
                        size: line.size,
                        quantity: line.quantity,
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="order-details-section" aria-label="Notes">
            <h3 className="order-details-section-title">Notes</h3>
            {order.notes ? (
              <p className="order-details-notes">{order.notes}</p>
            ) : (
              <p className="order-details-empty">No notes.</p>
            )}
          </section>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default OrderDetailsModal;
